import { KeplerColorMap, KeplerColorRange, KeplerScaleType } from 'features/builder/types'
import { hexToRGB } from 'features/common/utils/colors'

import { scaleQuantize, scaleQuantile, scaleOrdinal, scaleThreshold } from 'd3-scale'

export type FieldStats = {
  min: number
  max: number

  // quantiles, not including min and max
  quantiles?: Record<number | string, number[]>
}

type ScaleFactory = () => ScaleFunction
interface ScaleFunction<T = number[]> {
  (value: number): T
  domain(domain: Array<number | null>): ScaleFunction<T>
  range(range: Array<T>): ScaleFunction<T>
  unknown(value: T): ScaleFunction<T>
}

const scaleFunctionFactories: Partial<Record<KeplerScaleType, ScaleFactory>> = {
  [KeplerScaleType.Quantize]: scaleQuantize as ScaleFactory,
  [KeplerScaleType.Quantile]: scaleQuantile,
  [KeplerScaleType.Ordinal]: scaleOrdinal,
  [KeplerScaleType.Custom]: scaleThreshold
}

export const UNKNOWN_COLOR = [134, 141, 145]

export function createColorScaleFun({
  scaleType = KeplerScaleType.Quantize,
  colorRange,
  domain
}: {
  scaleType: KeplerScaleType
  colorRange: KeplerColorRange
  domain: Array<number | string | null>
}): (v: number) => number[] {
  const scaleFunFactory = scaleFunctionFactories[scaleType]
  if (!scaleFunFactory) {
    throw new Error(`Unknown scale type ${scaleType}`)
  }

  if (scaleType === KeplerScaleType.Custom && colorRange.uiCustomScaleType === 'logarithmic') {
    domain = getLog10ScaleSteps({ min: domain[0] as number, max: domain[1] as number, steps: colorRange.colors.length })
  } else if (scaleType === KeplerScaleType.Custom || scaleType === KeplerScaleType.Ordinal) {
    domain = colorRange.colorMap ? colorRange.colorMap.map(([key]) => key) : domain
  }

  const range = colorRange.colorMap
    ? colorRange.colorMap.map((entry) => hexToRGB(entry[1]))
    : colorRange.colors.map(hexToRGB)

  const scaleFun = scaleFunFactory()
    .domain(domain as number[])
    .range(range)
    .unknown(UNKNOWN_COLOR)
  return scaleFun
}

function roundedPow10(exp: number) {
  // Math.pow(10, less than 4) generates "0.0...009999999999999999" instead of "0.0...01"
  // round it ...
  const raw = Math.pow(10, exp)
  if (exp < 0) {
    const shift = Math.pow(10, -exp)
    return Math.round(raw * shift) / shift
  }
  return raw
}

export function getLog10ScaleSteps({
  min,
  max,
  steps
}: {
  min: number
  max: number
  steps: number
}): (number | null)[] {
  if (min === 0) {
    if (max === Infinity) {
      // count aggregations have [0, Infinity]
      // that will yield [10, 100, 1000, ...]
      return [...Array(steps)].map((_v, i) => (i === steps - 1 ? null : roundedPow10(i + 1)))
    }
    // if stats.min = 0, we only can attempt to start from max and decrease powers until
    // we use all color buckets ...
    const maxLog = Math.log10(max)
    const endExponent = Math.ceil(maxLog)
    const startExponent = endExponent - steps + 1
    return [...Array(steps)].map((_v, i) => (i === steps - 1 ? null : roundedPow10(startExponent + i)))
  } else {
    const minLog = Math.log10(min)
    const startExponent = Math.ceil(minLog) === minLog ? minLog + 1 : Math.ceil(minLog)

    return [...Array(steps)].map((_v, i) => (i === steps - 1 ? null : roundedPow10(startExponent + i)))
  }
}

export function getLog10ScaleColorMap({ min, max, colors }: { min: number; max: number; colors: string[] }) {
  return getLog10ScaleSteps({ min, max, steps: colors.length }).map((value, index): [number | null, string] => [
    value,
    colors[index]
  ])
}

export function colorMapsEqual(a: KeplerColorMap, b: KeplerColorMap) {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const [aKey, aColor] = a[i]
    const [bKey, bColor] = b[i]
    if (aKey !== bKey || aColor !== bColor) return false
  }
  return true
}

export function createColorDomain({
  scaleType,
  colorRange,
  stats
}: {
  scaleType: KeplerScaleType
  colorRange: KeplerColorRange
  stats?: FieldStats
}): Array<number | string | null> {
  if (scaleType === KeplerScaleType.Ordinal) {
    return colorRange.colorMap?.map(([value]) => value) || []
  }

  if (!stats) {
    return [0, 1]
  }
  if (scaleType === KeplerScaleType.Custom) {
    if (colorRange.uiCustomScaleType === 'logarithmic') {
      return [stats.min, stats.max]
    } else {
      // actually custom, read colorMap
      return colorRange.colorMap?.map(([value]) => value) || []
    }
  } else if (scaleType === KeplerScaleType.Quantile) {
    const steps = colorRange.colors.length
    const quantiles = stats.quantiles?.[steps]
    if (!quantiles) {
      return [0, 1]
    }
    // we expect quantiles domain in form [min, ...quantiles, max]
    if (quantiles.length < steps) {
      // some APIs (raster metadata) return quantiles as only _internal_ steps and we expect min and max here
      return [stats.min, ...quantiles, stats.max]
    } else {
      // some APIs (stats api) return quantiles _with_ min and max included
      return quantiles
    }
  } else {
    return [stats.min, stats.max]
  }
}
